import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { postData } from "../../utility/Utility";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import CustomLoader from "../../Components/CustomLoader/CustomLoader";

function EmployeeLogin() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    mobileNumber: "",
    otp: "",
    rememberMe: false
  });
  const [loading, setLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [mobileError, setMobileError] = useState("");

  // Check for saved mobile number on component mount
  useEffect(() => {
    const savedMobile = localStorage.getItem("rememberedMobile");
    if (savedMobile) {
      setFormData(prev => ({
        ...prev,
        mobileNumber: savedMobile,
        rememberMe: true
      }));
    }
  }, []);

  // Countdown timer for OTP resend
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const validateMobileNumber = (mobile) => {
    const mobileRegex = /^[0-9]{10}$/;
    if (!mobile) {
      return "Mobile number is required";
    } else if (!mobileRegex.test(mobile)) {
      return "Please enter a valid 10-digit mobile number";
    }
    return "";
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name === "mobileNumber") {
      // Allow only numbers and limit to 10 digits
      const numericValue = value.replace(/[^0-9]/g, '').slice(0, 10);
      setFormData({
        ...formData,
        [name]: numericValue
      });
      
      // Clear error when user starts typing
      if (mobileError && numericValue.length > 0) {
        setMobileError("");
      }
    } else {
      setFormData({
        ...formData,
        [name]: type === "checkbox" ? checked : value,
      });
    }
  };

  const handleSendOtp = async (e) => {
    e.preventDefault();
    
    const mobileError = validateMobileNumber(formData.mobileNumber);
    if (mobileError) {
      setMobileError(mobileError);
      return;
    }

    setLoading(true);
    try {
      const response = await postData("api/employees/login", {
        contactNumber: formData.mobileNumber
      });

      if (response?.status) {
        setOtpSent(true);
        setCountdown(60); // 60 seconds countdown
        toast.success("OTP sent successfully to your mobile!");
      } else {
        toast.error(response?.error || "Failed to send OTP. Please try again.");
      }
    } catch (error) {
      toast.error("An error occurred. Please try again later.");
      console.error("OTP sending error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (countdown > 0) return;

    setLoading(true);
    try {
      const response = await postData("api/employees/login", {
        contactNumber: formData.mobileNumber
      });

      if (response?.status) {
        setCountdown(60);
        toast.success("OTP resent successfully!");
      } else {
        toast.error(response?.error || "Failed to resend OTP.");
      }
    } catch (error) {
      toast.error("An error occurred. Please try again.");
      console.error("OTP resend error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!formData.otp) {
      toast.error("Please enter the OTP");
      return;
    }

    if (formData.otp.length !== 6) {
      toast.error("Please enter a valid 6-digit OTP");
      return;
    }

    setLoading(true);
    try {
      const response = await postData("api/employees/login-otp", {
        contactNumber: formData.mobileNumber,
        otp: formData.otp
      });

      if (response?.status) {
        localStorage.setItem("InsuranceAdminToken", response.token);

        if (formData.rememberMe) {
          localStorage.setItem("rememberedMobile", formData.mobileNumber);
        } else {
          localStorage.removeItem("rememberedMobile");
        }
        window.location.href="/home";
        toast.success("Login successful!");
          
      } else {
        toast.error(response?.error || "Login failed. Please try again.");
      }
    } catch (error) {
      toast.error("An error occurred. Please try again later.");
      console.error("Login error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleBackToMobile = () => {
    setOtpSent(false);
    setFormData(prev => ({ ...prev, otp: "" }));
  };

  return (
    <div className="login-page">
      <ToastContainer position="top-right" autoClose={3000} />
      <CustomLoader loading={loading} />
      <div className="account-page">
        <div className="container-fluid p-0">
          <div className="row g-0 vh-100">
            {/* Login Form Section */}
            <div className="col-xl-6">
              <div className="row">
                <div className="col-md-12 mx-auto log">
                  <div className="card card-border">
                    <div className="card-body">
                      <img
                        src="assets/images/insurance/logo-icon.png"
                        alt=""
                        className="top-bot"
                      />
                      <div className="mb-0 p-0 p-lg-3">
                        <div className="mb-0 border-0 p-md-4 p-lg-0 m-5">
                          <div className="p-0 text-lg-start text-center">
                            <div className="auth-brand">
                              <Link className="logo logo-dark" to="/">
                                <span className="logo-lg text-lg-center text-center">
                                  <h1>InsureSafe Employee</h1>
                                </span>
                              </Link>
                            </div>
                          </div>

                          <div className="auth-title-section mt-4 mb-4 text-lg-center text-center">
                            <h4 className="text-dark fw-semibold mb-3">
                              Welcome Back 
                            </h4>
                            <p className="text-muted">
                              {otpSent ? "Enter OTP sent to your mobile" : "Access the insurance management system"}
                            </p>
                          </div>

                          <div className="pt-0">
                            <form className="my-4" onSubmit={otpSent ? handleLogin : handleSendOtp}>
                              {/* Mobile Number Input (Step 1) */}
                              {!otpSent && (
                                <div className="form-group mb-3">
                                  <label htmlFor="mobileNumber" className="form-label">
                                    Mobile Number*
                                  </label>
                                  <input
                                    className={`form-control ${mobileError ? "is-invalid" : ""}`}
                                    type="tel"
                                    id="mobileNumber"
                                    name="mobileNumber"
                                    value={formData.mobileNumber}
                                    onChange={handleInputChange}
                                    required
                                    maxLength="10"
                                    placeholder="Enter your 10-digit mobile number"
                                    disabled={loading}
                                  />
                                  {mobileError && (
                                    <div className="invalid-feedback d-block">
                                      {mobileError}
                                    </div>
                                  )}
                                  <small className="text-muted">
                                    Enter your registered mobile number
                                  </small>
                                </div>
                              )}

                              {/* OTP Input (Step 2) */}
                              {otpSent && (
                                <div className="form-group mb-3">
                                  <label htmlFor="otp" className="form-label">
                                    Enter OTP*
                                  </label>
                                  <input
                                    className="form-control"
                                    type="text"
                                    id="otp"
                                    name="otp"
                                    value={formData.otp}
                                    onChange={handleInputChange}
                                    required
                                    maxLength="6"
                                    placeholder="Enter 6-digit OTP"
                                    disabled={loading}
                                  />
                                  <div className="d-flex justify-content-between align-items-center mt-2">
                                    <small className="text-muted">
                                      OTP sent to +91 {formData.mobileNumber}
                                    </small>
                                    <button
                                      type="button"
                                      className="btn btn-link p-0 text-primary fs-14"
                                      onClick={handleBackToMobile}
                                      disabled={loading}
                                    >
                                      Change number
                                    </button>
                                  </div>
                                </div>
                              )}

                              {/* Remember Me Checkbox */}
                              {!otpSent && (
                                <div className="form-group d-flex mb-3">
                                  <div className="col-sm-6">
                                    <div className="form-check">
                                      <input
                                        type="checkbox"
                                        className="form-check-input"
                                        id="rememberMe"
                                        name="rememberMe"
                                        checked={formData.rememberMe}
                                        onChange={handleInputChange}
                                        disabled={loading}
                                      />
                                      <label
                                        className="form-check-label"
                                        htmlFor="rememberMe"
                                      >
                                        Remember me
                                      </label>
                                    </div>
                                  </div>
                                </div>
                              )}

                              {/* Resend OTP Link */}
                              {otpSent && (
                                <div className="form-group mb-3 text-center">
                                  <span className="text-muted me-2">Didn't receive OTP?</span>
                                  <button
                                    type="button"
                                    className={`btn btn-link p-0 fs-14 ${countdown > 0 ? "text-muted" : "text-primary"}`}
                                    onClick={handleResendOtp}
                                    disabled={countdown > 0 || loading}
                                  >
                                    {countdown > 0 ? `Resend OTP in ${countdown}s` : "Resend OTP"}
                                  </button>
                                </div>
                              )}

                              {/* Submit Button */}
                              <div className="form-group mb-0 mt-3 row">
                                <div className="col-12 mt-5">
                                  <div className="d-grid">
                                    <button
                                      className="btn btn-primary fw-semibold"
                                      type="submit"
                                      disabled={loading}
                                    >
                                      {loading ? (
                                        <>
                                          <span
                                            className="spinner-border spinner-border-sm me-2"
                                            role="status"
                                            aria-hidden="true"
                                          ></span>
                                          {otpSent ? "Verifying..." : "Sending OTP..."}
                                        </>
                                      ) : (
                                        otpSent ? "Login to Dashboard" : "Send OTP"
                                      )}
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </form>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Side Background Section */}
            <div className="col-xl-6 d-none d-xl-inline-block log-img-right insurance-bg">
              {/* Insurance-themed background image section */}
              <div className="insurance-bg-overlay"></div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
       
        
        
        
        .top-bot {
          display: block;
          margin: 0 auto 20px;
          max-width: 80px;
        }
        
        .card-border {
          border: none;
          box-shadow: 0 0 20px rgba(0, 0, 0, 0.1);
          border-radius: 15px;
        }
        
        .btn-primary {
          background: linear-gradient(135deg, #1e3c72 0%, #2a5298 100%);
          border: none;
          padding: 12px;
          border-radius: 8px;
        }
        
        .btn-primary:hover {
          background: linear-gradient(135deg, #2a5298 0%, #1e3c72 100%);
          transform: translateY(-1px);
          transition: all 0.3s ease;
        }
        
        .form-control {
          border-radius: 8px;
          padding: 12px;
          border: 1px solid #e0e0e0;
        }
        
        .form-control:focus {
          border-color: #1e3c72;
          box-shadow: 0 0 0 0.2rem rgba(30, 60, 114, 0.25);
        }
      `}</style>
    </div>
  );
}

export default EmployeeLogin;